// src/components/PrintDesigner/PropertiesPanel.jsx
// لوحة الخصائص: خصائص العنصر المحدد، أو إعدادات الجدول، أو إعدادات الصفحة
import React, { useRef, useState } from "react";
import {
  Box, Typography, TextField, MenuItem, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, Switch,
  FormControlLabel, Divider, Button, Menu, ListSubheader,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FlipToFrontIcon from "@mui/icons-material/FlipToFront";
import FlipToBackIcon from "@mui/icons-material/FlipToBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import AlignHorizontalCenterIcon from "@mui/icons-material/AlignHorizontalCenter";
import { toast } from "react-toastify";
import { VARIABLE_GROUPS } from "./printVariables";
import { PAPER, contentWidth } from "./printTemplates";

const ACCENT = "#4F46E5";
const fs = { "& .MuiOutlinedInput-root": { borderRadius: "9px", fontSize: 13, bgcolor: "#fff" }, "& .MuiInputLabel-root": { fontSize: 13 } };

const TYPE_LABEL = { text: "نص", image: "صورة", logo: "شعار المنشأة", qr: "رمز QR", line: "خط", rect: "مستطيل", totals: "الإجماليات", html: "HTML متقدم" };

// ───────────────────────── عناصر إدخال صغيرة ─────────────────────────
const Group = ({ title, children }) => (
  <Box sx={{ px: 1.75, py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
    {title && <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#64748b", mb: 1 }}>{title}</Typography>}
    <Box sx={{ display: "grid", gap: 1 }}>{children}</Box>
  </Box>
);
const Row = ({ children, cols = 2 }) => (
  <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>{children}</Box>
);

function Num({ label, value, onChange, step = 0.5, min, max, suffix }) {
  return (
    <TextField
      size="small" type="number" label={label} value={value ?? ""} sx={fs}
      onChange={e => {
        if (e.target.value === "") return onChange(min ?? 0);
        let v = parseFloat(e.target.value);
        if (Number.isNaN(v)) return;
        if (min != null) v = Math.max(min, v);
        if (max != null) v = Math.min(max, v);
        onChange(v);
      }}
      inputProps={{ step, min, max, style: { direction: "ltr", textAlign: "right" } }}
      InputProps={suffix ? { endAdornment: <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{suffix}</Typography> } : undefined}
    />
  );
}

function Color({ label, value, onChange, allowEmpty = true }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: .75, border: "1px solid #e5e7eb", borderRadius: "9px", px: 1, height: 40, bgcolor: "#fff" }}>
      <Box component="label" sx={{
        width: 24, height: 24, borderRadius: "6px", border: "1px solid #cbd5e1", cursor: "pointer", flexShrink: 0, position: "relative",
        background: value || "repeating-linear-gradient(45deg,#fff 0 3px,#e5e7eb 3px 6px)",
      }}>
        <input type="color" value={value || "#ffffff"} onChange={e => onChange(e.target.value)}
          style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer" }} />
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "#475569", flex: 1, minWidth: 0 }} noWrap>{label}</Typography>
      {allowEmpty && value && (
        <Typography component="button" onClick={() => onChange("")}
          sx={{ border: 0, bgcolor: "transparent", color: "#94a3b8", fontSize: 11, cursor: "pointer", p: 0 }}>بدون</Typography>
      )}
    </Box>
  );
}

function Sel({ label, value, onChange, options }) {
  return (
    <TextField select size="small" label={label} value={value ?? ""} onChange={e => onChange(e.target.value)} sx={fs}>
      {options.map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{l}</MenuItem>)}
    </TextField>
  );
}

const tbSx = { "& .MuiToggleButton-root": { py: .5, px: 1, borderRadius: "8px !important", border: "1px solid #e5e7eb !important", mx: .2 },
  "& .Mui-selected": { bgcolor: `${ACCENT}14 !important`, color: `${ACCENT} !important` } };

// زر إدراج متغير في مكان المؤشر
function VariableMenu({ onPick, label = "إدراج متغير" }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Button size="small" variant="outlined" startIcon={<DataObjectIcon />} onClick={e => setAnchor(e.currentTarget)}
        sx={{ borderRadius: "9px", fontWeight: 700, "& .MuiButton-startIcon": { ml: .5, mr: -.25 } }}>
        {label}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { maxHeight: 420, borderRadius: "12px", minWidth: 240 } }}>
        {VARIABLE_GROUPS.map(g => [
          <ListSubheader key={g.key} sx={{ fontWeight: 800, lineHeight: "32px", color: ACCENT }}>{g.label}</ListSubheader>,
          ...g.items.map(v => (
            <MenuItem key={v.key} onClick={() => { onPick(v); setAnchor(null); }} sx={{ fontSize: 13, justifyContent: "space-between", gap: 2 }}>
              <span>{v.label}</span>
              <Typography component="span" sx={{ fontSize: 11, color: "#94a3b8", direction: "ltr" }}>{`{${v.key}}`}</Typography>
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
}

// ======================================================================
export default function PropertiesPanel({
  layout, selected, onChangeElement, onDelete, onDuplicate, onArrange, onChangeLayout,
}) {
  if (selected === "table") return <TableProps layout={layout} onChangeLayout={onChangeLayout} />;
  if (!selected) return <PageProps layout={layout} onChangeLayout={onChangeLayout} />;

  const el = selected;
  const p = el.props || {};
  const set = (patch) => onChangeElement(el.id, patch);
  const setP = (patch) => onChangeElement(el.id, { props: { ...p, ...patch } });
  const W = contentWidth(layout);

  return (
    <Box>
      <Box sx={{ px: 1.75, py: 1.25, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #e5e7eb", bgcolor: "#fafbfc" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14, flex: 1 }}>{TYPE_LABEL[el.type] || el.type}</Typography>
        <Tooltip title={el.locked ? "فك القفل" : "قفل (منع التحريك)"}>
          <IconButton size="small" onClick={() => set({ locked: !el.locked })}>{el.locked ? <LockOutlinedIcon fontSize="small" /> : <LockOpenOutlinedIcon fontSize="small" />}</IconButton>
        </Tooltip>
        <Tooltip title="تكرار (Ctrl+D)"><IconButton size="small" onClick={onDuplicate}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="حذف (Delete)"><IconButton size="small" onClick={onDelete} sx={{ color: "#e11d48" }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
      </Box>

      {el.type === "text" && <TextProps p={p} setP={setP} />}
      {el.type === "html" && (
        <Group title="محتوى HTML">
          <VariableMenu onPick={v => setP({ html: `${p.html || ""}{${v.key}}` })} />
          <TextField multiline minRows={6} size="small" value={p.html || ""} onChange={e => setP({ html: e.target.value })}
            sx={{ ...fs, "& textarea": { fontFamily: "monospace", direction: "ltr", fontSize: 12 } }} />
        </Group>
      )}
      {(el.type === "image" || el.type === "logo") && <ImageProps el={el} p={p} setP={setP} set={set} />}
      {el.type === "qr" && (
        <Group title="رمز QR">
          <Typography sx={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.7 }}>
            يُعرض رمز الفاتورة الإلكترونية (هيئة الزكاة) تلقائيًا عند الطباعة. اجعل العرض والارتفاع متساويين.
          </Typography>
        </Group>
      )}
      {el.type === "line" && (
        <Group title="الخط">
          <Row>
            <Sel label="الاتجاه" value={p.orientation} onChange={v => setP({ orientation: v })} options={[["horizontal", "أفقي"], ["vertical", "عمودي"]]} />
            <Sel label="النمط" value={p.style} onChange={v => setP({ style: v })} options={[["solid", "متصل"], ["dashed", "متقطع"], ["dotted", "منقّط"], ["double", "مزدوج"]]} />
          </Row>
          <Row>
            <Num label="السماكة" value={p.thickness} onChange={v => setP({ thickness: v })} min={0.5} max={10} suffix="px" />
            <Color label="اللون" value={p.color} onChange={v => setP({ color: v || "#111827" })} allowEmpty={false} />
          </Row>
        </Group>
      )}
      {el.type === "rect" && (
        <Group title="المستطيل">
          <Row>
            <Num label="الحد" value={p.borderWidth} onChange={v => setP({ borderWidth: v })} min={0} max={10} suffix="px" />
            <Num label="الانحناء" value={p.radius} onChange={v => setP({ radius: v })} min={0} max={20} suffix="mm" />
          </Row>
          <Row>
            <Color label="لون الحد" value={p.borderColor} onChange={v => setP({ borderColor: v || "#111827" })} allowEmpty={false} />
            <Color label="التعبئة" value={p.bg} onChange={v => setP({ bg: v })} />
          </Row>
          <Sel label="نمط الحد" value={p.borderStyle} onChange={v => setP({ borderStyle: v })} options={[["solid", "متصل"], ["dashed", "متقطع"], ["dotted", "منقّط"]]} />
        </Group>
      )}
      {el.type === "totals" && <TotalsProps p={p} setP={setP} />}

      {/* الموضع والحجم */}
      <Group title="الموضع والحجم (مم)">
        <Row>
          <Num label="من اليسار X" value={el.x} onChange={v => set({ x: v })} />
          <Num label="من الأعلى Y" value={el.y} onChange={v => set({ y: v })} min={0} />
        </Row>
        <Row>
          <Num label="العرض" value={el.w} onChange={v => set({ w: v })} min={0.5} />
          <Num label="الارتفاع" value={el.h} onChange={v => set({ h: v })} min={0.5} />
        </Row>
        <Row cols={3}>
          <Tooltip title="توسيط أفقي في الصفحة">
            <Button size="small" variant="outlined" onClick={() => set({ x: Math.round(((W - el.w) / 2) * 10) / 10 })} sx={{ borderRadius: "9px", minWidth: 0 }}>
              <AlignHorizontalCenterIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="إحضار للأمام"><Button size="small" variant="outlined" onClick={() => onArrange("front")} sx={{ borderRadius: "9px", minWidth: 0 }}><FlipToFrontIcon fontSize="small" /></Button></Tooltip>
          <Tooltip title="إرسال للخلف"><Button size="small" variant="outlined" onClick={() => onArrange("back")} sx={{ borderRadius: "9px", minWidth: 0 }}><FlipToBackIcon fontSize="small" /></Button></Tooltip>
        </Row>
        <Sel label="النطاق" value={el.band} onChange={v => set({ band: v, y: 0 })} options={[["header", "رأس الصفحة"], ["footer", "ذيل الصفحة"]]} />
      </Group>
    </Box>
  );
}

// ───────────────────────── نص ─────────────────────────
function TextProps({ p, setP }) {
  const ref = useRef(null);
  const insert = (v) => {
    const ta = ref.current;
    const token = `{${v.key}}`;
    if (!ta) return setP({ text: `${p.text || ""}${token}` });
    const s = ta.selectionStart ?? ta.value.length, e = ta.selectionEnd ?? s;
    const text = ta.value.slice(0, s) + token + ta.value.slice(e);
    setP({ text });
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(s + token.length, s + token.length); });
  };
  const fmt = [p.bold && "bold", p.italic && "italic", p.underline && "underline"].filter(Boolean);
  return (
    <>
      <Group title="المحتوى">
        <TextField
          inputRef={ref} multiline minRows={3} maxRows={10} size="small" value={p.text || ""} dir={p.dir || "rtl"}
          onChange={e => setP({ text: e.target.value })} sx={fs}
          helperText="المتغيرات بين { } تُستبدل بالبيانات. نقر مزدوج على العنصر للتحرير المباشر."
        />
        <VariableMenu onPick={insert} />
      </Group>
      <Group title="الخط">
        <Row>
          <Num label="الحجم" value={p.fontSize} onChange={v => setP({ fontSize: v })} min={6} max={72} suffix="px" />
          <Color label="اللون" value={p.color} onChange={v => setP({ color: v || "#111827" })} allowEmpty={false} />
        </Row>
        <Box sx={{ display: "flex", gap: .75, flexWrap: "wrap" }}>
          <ToggleButtonGroup size="small" value={fmt} sx={tbSx}
            onChange={(_, v) => setP({ bold: v.includes("bold"), italic: v.includes("italic"), underline: v.includes("underline") })}>
            <ToggleButton value="bold"><FormatBoldIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="italic"><FormatItalicIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="underline"><FormatUnderlinedIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={p.align} onChange={(_, v) => v && setP({ align: v })} sx={tbSx}>
            <ToggleButton value="right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="center"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={p.vAlign} onChange={(_, v) => v && setP({ vAlign: v })} sx={tbSx}>
            <ToggleButton value="top"><VerticalAlignTopIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="center"><VerticalAlignCenterIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="bottom"><VerticalAlignBottomIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Row>
          <Sel label="الاتجاه" value={p.dir} onChange={v => setP({ dir: v })} options={[["rtl", "عربي (يمين ← يسار)"], ["ltr", "English (LTR)"]]} />
          <Num label="تباعد الأسطر" value={p.lineHeight} onChange={v => setP({ lineHeight: v })} step={0.1} min={0.8} max={3} />
        </Row>
      </Group>
      <Group title="الإطار والخلفية">
        <Row>
          <Color label="الخلفية" value={p.bg} onChange={v => setP({ bg: v })} />
          <Color label="لون الحد" value={p.borderColor} onChange={v => setP({ borderColor: v || "#111827" })} allowEmpty={false} />
        </Row>
        <Row cols={3}>
          <Num label="الحد" value={p.borderWidth} onChange={v => setP({ borderWidth: v })} min={0} max={10} suffix="px" />
          <Num label="انحناء" value={p.radius} onChange={v => setP({ radius: v })} min={0} max={20} />
          <Num label="حشوة" value={p.padding} onChange={v => setP({ padding: v })} min={0} max={20} />
        </Row>
      </Group>
    </>
  );
}

// ───────────────────────── صورة ─────────────────────────
function ImageProps({ el, p, setP, set }) {
  const upload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("حجم الصورة أكبر من 1 ميجابايت"); return; }
    const r = new FileReader();
    r.onload = () => { set({ type: "image", props: { ...p, source: "upload", src: r.result } }); };
    r.readAsDataURL(file);
  };
  return (
    <Group title="الصورة">
      <Sel label="المصدر" value={el.type === "logo" ? "companyLogo" : "upload"}
        onChange={v => set(v === "companyLogo" ? { type: "logo", props: { ...p, source: "companyLogo" } } : { type: "image", props: { ...p, source: "upload" } })}
        options={[["companyLogo", "شعار المنشأة (من بياناتها)"], ["upload", "صورة مرفوعة"]]} />
      {el.type === "image" && (
        <Button component="label" variant="outlined" size="small" startIcon={<UploadOutlinedIcon />}
          sx={{ borderRadius: "9px", fontWeight: 700, "& .MuiButton-startIcon": { ml: .5, mr: -.25 } }}>
          {p.src ? "استبدال الصورة" : "رفع صورة"} (حتى 1MB)
          <input type="file" hidden accept="image/*" onChange={upload} />
        </Button>
      )}
      <Row>
        <Sel label="الملاءمة" value={p.fit} onChange={v => setP({ fit: v })} options={[["contain", "احتواء"], ["cover", "ملء"], ["fill", "تمديد"]]} />
        <Num label="انحناء" value={p.radius} onChange={v => setP({ radius: v })} min={0} max={30} suffix="mm" />
      </Row>
    </Group>
  );
}

// ───────────────────────── الإجماليات ─────────────────────────
function TotalsProps({ p, setP }) {
  const rows = p.rows || [];
  const setRow = (i, patch) => setP({ rows: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <Group title="بنود الإجماليات">
      {rows.map((r, i) => (
        <Box key={r.key} sx={{ display: "flex", alignItems: "center", gap: .5 }}>
          <IconButton size="small" onClick={() => setRow(i, { visible: !r.visible })} sx={{ color: r.visible ? ACCENT : "#cbd5e1" }}>
            {r.visible ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
          </IconButton>
          <TextField size="small" value={r.labelAr} onChange={e => setRow(i, { labelAr: e.target.value })} sx={{ ...fs, flex: 1 }} disabled={!r.visible} />
          <TextField size="small" value={r.labelEn} onChange={e => setRow(i, { labelEn: e.target.value })} sx={{ ...fs, flex: 1, "& input": { direction: "ltr" } }} disabled={!r.visible || !p.bilingual} />
        </Box>
      ))}
      <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={!!p.bilingual} onChange={e => setP({ bilingual: e.target.checked })} />}
        label={<Typography sx={{ fontSize: 13 }}>عناوين بالعربي والإنجليزي</Typography>} />
      <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={!!p.border} onChange={e => setP({ border: e.target.checked })} />}
        label={<Typography sx={{ fontSize: 13 }}>حدود للخلايا</Typography>} />
      <Row>
        <Num label="حجم الخط" value={p.fontSize} onChange={v => setP({ fontSize: v })} min={6} max={24} suffix="px" />
        <Color label="خلفية الصافي" value={p.netBg} onChange={v => setP({ netBg: v })} />
      </Row>
      <Color label="لون نص الصافي" value={p.netColor} onChange={v => setP({ netColor: v || "#111827" })} allowEmpty={false} />
    </Group>
  );
}

// ───────────────────────── الجدول ─────────────────────────
function TableProps({ layout, onChangeLayout }) {
  const t = layout.table;
  const [drag, setDrag] = useState(null);
  const setT = (patch) => onChangeLayout({ ...layout, table: { ...t, ...patch } });
  const setCol = (i, patch) => setT({ columns: t.columns.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const move = (from, to) => {
    if (from === to || from == null) return;
    const cols = [...t.columns];
    const [it] = cols.splice(from, 1);
    cols.splice(to, 0, it);
    setT({ columns: cols });
  };
  const visibleW = t.columns.filter(c => c.visible).reduce((s, c) => s + (Number(c.width) || 0), 0);

  return (
    <Box>
      <Box sx={{ px: 1.75, py: 1.25, borderBottom: "1px solid #e5e7eb", bgcolor: "#fafbfc" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>جدول الأصناف</Typography>
      </Box>
      <Group title={`الأعمدة — اسحب للترتيب (العرض نسبي، المجموع ${visibleW})`}>
        {t.columns.map((c, i) => (
          <Box
            key={c.key} draggable
            onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); move(drag, i); setDrag(null); }}
            sx={{
              display: "grid", gridTemplateColumns: "18px 30px minmax(0,1fr) minmax(0,1fr) 52px", gap: .5, alignItems: "center",
              p: .5, borderRadius: "9px", border: "1px solid #e5e7eb", bgcolor: drag === i ? "#eef2ff" : c.visible ? "#fff" : "#f8fafc",
              opacity: c.visible ? 1 : .6,
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 18, color: "#94a3b8", cursor: "grab" }} />
            <IconButton size="small" onClick={() => setCol(i, { visible: !c.visible })} sx={{ color: c.visible ? ACCENT : "#94a3b8" }}>
              {c.visible ? <VisibilityOutlinedIcon fontSize="small" /> : <VisibilityOffOutlinedIcon fontSize="small" />}
            </IconButton>
            <TextField size="small" value={c.labelAr} onChange={e => setCol(i, { labelAr: e.target.value })} sx={fs} />
            <TextField size="small" value={c.labelEn} onChange={e => setCol(i, { labelEn: e.target.value })} sx={{ ...fs, "& input": { direction: "ltr" } }} />
            <TextField size="small" type="number" value={c.width} onChange={e => setCol(i, { width: Math.max(1, Number(e.target.value) || 1) })}
              sx={fs} inputProps={{ min: 1, style: { direction: "ltr", textAlign: "center", padding: "8.5px 4px" } }} />
          </Box>
        ))}
      </Group>
      <Group title="التنسيق">
        <Row>
          <Num label="حجم الخط" value={t.fontSize} onChange={v => setT({ fontSize: v })} min={6} max={20} suffix="px" />
          <Sel label="الحدود" value={t.border} onChange={v => setT({ border: v })} options={[["grid", "شبكة كاملة"], ["rows", "بين الصفوف"], ["none", "بدون"]]} />
        </Row>
        <Row>
          <Color label="خلفية العناوين" value={t.headerBg} onChange={v => setT({ headerBg: v })} />
          <Color label="لون العناوين" value={t.headerColor} onChange={v => setT({ headerColor: v || "#111827" })} allowEmpty={false} />
        </Row>
        {[["bilingual", "عناوين بالعربي والإنجليزي"], ["zebra", "تظليل الصفوف بالتناوب"], ["showDescription", "إظهار وصف الصنف تحت اسمه"]].map(([k, l]) => (
          <FormControlLabel key={k} sx={{ m: 0 }} control={<Switch size="small" checked={!!t[k]} onChange={e => setT({ [k]: e.target.checked })} />}
            label={<Typography sx={{ fontSize: 13 }}>{l}</Typography>} />
        ))}
      </Group>
    </Box>
  );
}

// ───────────────────────── الصفحة ─────────────────────────
function PageProps({ layout, onChangeLayout }) {
  const pg = layout.page;
  const setPage = (patch) => onChangeLayout({ ...layout, page: { ...pg, ...patch } });
  const setBand = (band, h) => onChangeLayout({ ...layout, bands: { ...layout.bands, [band]: { ...layout.bands[band], height: h } } });
  return (
    <Box>
      <Box sx={{ px: 1.75, py: 1.25, borderBottom: "1px solid #e5e7eb", bgcolor: "#fafbfc" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>إعدادات الصفحة</Typography>
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>اختر عنصرًا من الصفحة لتعديل خصائصه</Typography>
      </Box>
      <Group title="الورق">
        <Sel label="المقاس" value={pg.size} onChange={v => setPage({ size: v })} options={Object.entries(PAPER).map(([k, v]) => [k, v.label])} />
        <Row>
          <Num label="الهوامش" value={pg.margin} onChange={v => setPage({ margin: v })} min={0} max={30} suffix="mm" />
          <Num label="حجم الخط العام" value={pg.fontSize} onChange={v => setPage({ fontSize: v })} min={6} max={20} suffix="px" />
        </Row>
      </Group>
      <Group title="المحتوى">
        <FormControlLabel sx={{ m: 0 }}
          control={<Switch size="small" checked={layout.table?.enabled !== false}
            onChange={e => onChangeLayout({ ...layout, table: { ...layout.table, enabled: e.target.checked } })} />}
          label={<Typography sx={{ fontSize: 13 }}>جدول الأصناف بين الرأس والذيل</Typography>} />
      </Group>
      <Group title="ارتفاع النطاقات (مم)">
        <Row>
          <Num label="رأس الصفحة" value={layout.bands.header.height} onChange={v => setBand("header", v)} min={5} />
          <Num label="ذيل الصفحة" value={layout.bands.footer.height} onChange={v => setBand("footer", v)} min={5} />
        </Row>
        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>أو اسحب الشريط أسفل كل نطاق</Typography>
      </Group>
      <Group title="CSS إضافي (متقدم)">
        <TextField multiline minRows={4} size="small" value={layout.customCss || ""} placeholder=".pl-page { }"
          onChange={e => onChangeLayout({ ...layout, customCss: e.target.value })}
          sx={{ ...fs, "& textarea": { fontFamily: "monospace", direction: "ltr", fontSize: 12 } }} />
      </Group>
      <Divider />
      <Group title="اختصارات لوحة المفاتيح">
        {[["Delete", "حذف"], ["Ctrl + D", "تكرار"], ["Ctrl + Z / Y", "تراجع / إعادة"], ["الأسهم", "تحريك 1 مم (Shift: 5 مم)"], ["Alt أثناء السحب", "إلغاء الالتصاق"], ["Ctrl + S", "حفظ"], ["نقر مزدوج", "تحرير النص"]].map(([k, v]) => (
          <Box key={k} sx={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
            <Typography sx={{ fontSize: 12.5, color: "#475569" }}>{v}</Typography>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#334155", bgcolor: "#f1f5f9", px: .75, borderRadius: "5px", direction: "ltr" }}>{k}</Typography>
          </Box>
        ))}
      </Group>
    </Box>
  );
}
